import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['title', 'frame']
  static values = {
    disableBackdropClose: Boolean,
    resetOnClose: Boolean,
    frameId: String,
    titleId: String,
  }

  DIALOG_OPEN_CLASS = 'components--modal__dialog--open'

  // attributes that should not be copied and overwritten onto the dialog
  NO_COPY_ATTRS = ['id', 'closeby', 'hidden', 'aria-modal']

  connect() {
    this.element.addEventListener('turbo:frame-load', this.handleFrameLoad)
    // use 'mousedown' instead of click to prevent button clicks that 
    // accidentally drag and non-mouse 'click' triggers
    this.element.addEventListener('mousedown', this._onMouseDown)
    this.element.addEventListener('close', this._onClose)
    this.element.addEventListener('turbo:submit-end', this._onSubmitEnd)
    this.element.addEventListener('turbo:click', this._onTurboClick)
    document.addEventListener('turbo:before-cache', this._onBeforeCache)
  }

  disconnect() {
    document.removeEventListener('turbo:before-cache', this._onBeforeCache)
  }

  open(modalController) {
    this.modalController = modalController
    // in rare cases a modal can be opened before the previous modal has reset (200ms gap)
    // to account for this we manually reset here if we are currently waiting to reset
    if (this.waitingToReset) this._reset()

    const dialogContent = this.getContent()
    dialogContent.childNodes.forEach((node) => this.element.appendChild(node.cloneNode(true)))
    // value for select elements is not cloned by `cloneNode`, so we must manually set value
    dialogContent.querySelectorAll('select').forEach(input => {
      const dialogInput = this.element.querySelector(`[name="${input.name}"]`)
      if (dialogInput) dialogInput.value = input.value
    })

    ReactRailsUJS.mountComponents(this.element)

    // copy valid attributes from modal instance onto singleton modal
    const attributes = Array.from(dialogContent.attributes)
    for (const attr of attributes) {
      if (this.NO_COPY_ATTRS.includes(attr.name)) continue

      if (attr.name === 'data-controller') {
        const existingController = this.element.getAttribute('data-controller')
        if (!existingController.includes(attr.value)) {
          this.element.setAttribute('data-controller', `${existingController} ${attr.value}`)
        }
        continue
      }

      this.element.setAttribute(attr.name, attr.value)
    }

    if (this.hasTitleTarget) {
      this.titleTarget.setAttribute('id', this.titleIdValue)
    }

    if (this.hasFrameTarget) {
      this.frameTarget.setAttribute('id', this.frameIdValue)
    }

    this.element.hidden = false

    requestAnimationFrame(() => {
      this.element.classList.add(this.DIALOG_OPEN_CLASS)
      this.element.showModal()
    })
  }

  handleClose = () => {
    if ('requestClose' in this.element) {
      this.element.requestClose()
    } else {
      this.element.close()
    }
  }

  handleFrameLoad = e => {
    const frameTitleEl = e.target.querySelector('[data-frame-title]')
    if (frameTitleEl && this.hasTitleTarget) {
      this.titleTarget.textContent = frameTitleEl.getAttribute('data-frame-title')
    }
  }

  getContent = () => {
    if (!this.modalController) return

    return this.modalController.dialogContentTarget
  }

  // private helpers

  _getById = (id) => {
    return this.element.querySelector(`#${id}`)
  }

  _reset = () => {
    this.element.hidden = true
    const shouldReset = this.shouldResetContent || this.resetOnCloseValue
    // update template content UNLESS reset on close is set
    const dialogContent = this.getContent()
    if (!shouldReset && dialogContent != null) {
      // if we are replacing the template we need to remove these IDs so that if the modal is reopened,
      // there will not be duplicate IDs in the document
      // enforced by spec: spec/system/components/ui/modal_component_spec.rb:26
      if (this.hasTitleTarget) this.titleTarget.removeAttribute('id')
      if (this.hasFrameTarget) this.frameTarget.removeAttribute('id')

      dialogContent.replaceChildren(...this.element.childNodes)
    }
    this.element.innerHTML = ''
    this.waitingToReset = false
    this.shouldResetContent = false
  }

  // private event handlers

  _onMouseDown = event => {
    const { target, currentTarget, clientY, clientX } = event

    if (!this.disableBackdropCloseValue) {
      // do not close IF click coords are within bounds of modal dialog
      const rect = currentTarget.getBoundingClientRect()
      const isInDialog =
        rect.top <= clientY &&
        rect.left <= clientX &&
        clientY <= rect.top + rect.height &&
        clientX <= rect.left + rect.width
      if (isInDialog) return;
      // do not close IF click is outside bounds, but target is contained by modal (e.g. datepicker, dropdown)
      if (currentTarget !== target && currentTarget.contains(target)) return;

      this.handleClose()
    }
  }

  _onClose = () => {
    this.element.classList.remove(this.DIALOG_OPEN_CLASS)
    this.waitingToReset = true
    // give a bit of time for animation to play before hiding
    setTimeout(() => {
      if (this.waitingToReset) this._reset()
    }, 200)
    document.activeElement.focus({ focusVisible: true })
    this.modalController = null
  }

  _onSubmitEnd = e => {
    // reset turbo frame content on successful form submission, so that the DOM resets
    // this is designed to work whether the form is rendered with the layout or loaded asynchronously
    const { success, fetchResponse } = e.detail
    if (success) {
      const keepOpenHeaderValue = fetchResponse.response.headers.get('x-monami-modal-keep-open')
      if (!keepOpenHeaderValue) {
        this.shouldResetContent = true
        this.handleClose()
      }
    }
  }

  _onTurboClick = ({ target }) => {
    const newTitle = target.getAttribute('data-modal-title')
    if (newTitle && this.hasTitleTarget) {
      this.titleTarget.innerHTML = newTitle
    }
  }

  _onBeforeCache = () => {
    // before cache, reset modal to be reinitialized by template
    this.element.innerHTML = ''
    this.element.classList.remove(this.DIALOG_OPEN_CLASS)
    this.element.hidden = true
    this.element.close()
  }
}
